import { type Form } from '@wix/ambassador-forms-v4-form/types';
import {
  getFormSchedulingFields,
  getFormServicesDropdownFields,
  getSchedulingData,
  getServicesData,
  populateSchedulingFields,
  populateServicesDropdownFields,
} from './scheduling';
import type { IHttpClient } from '@wix/yoshi-flow-editor';
import type { ErrorHandlerPublicAPI } from '@wix/fe-essentials-editor/error-handler';
import {
  isServicesRequiredForTemplateId,
  isSchedulingRequiredForTemplateId,
} from '../templates/templates';
import { type Service } from '@wix/ambassador-bookings-services-v2-service/types';
import { type StaffMember } from '@wix/ambassador-bookings-staff-v1-staff-member/types';
import { type HandlePresetTemplateRequirements } from './utils';

interface DataForFieldPopulation {
  servicesData?: { services: Service[] };
  schedulingData?: { staffMember?: StaffMember };
}

export interface PopulateFormFieldsParams {
  form: Form;
  httpClient: IHttpClient;
  errorHandler: ErrorHandlerPublicAPI;
  linguistHeader: string;
  dataForFieldPopulation: DataForFieldPopulation;
}

export const populateFormFields = async (args: PopulateFormFieldsParams) => {
  const { form } = args;
  const populationFunctions = [];
  if (getFormSchedulingFields(form)?.length > 0) {
    populationFunctions.push(populateSchedulingFields);
  }
  if (getFormServicesDropdownFields(form)?.length > 0) {
    populationFunctions.push(populateServicesDropdownFields);
  }
  await Promise.all(populationFunctions.map((fn) => fn(args)));
};

interface FetchDataForFieldPopulationParams {
  httpClient: IHttpClient;
  errorHandler: ErrorHandlerPublicAPI;
  linguistHeader: string;
  templateId: string;
  handlePresetTemplateRequirements?: HandlePresetTemplateRequirements;
}

export const fetchDataForFieldPopulation = async (
  args: FetchDataForFieldPopulationParams,
) => {
  const {
    httpClient,
    errorHandler,
    linguistHeader,
    templateId,
    handlePresetTemplateRequirements,
  } = args;
  const result: DataForFieldPopulation = {};
  const fetchDataPromises = [];
  if (isServicesRequiredForTemplateId(templateId)) {
    fetchDataPromises.push(
      (async () => {
        await handlePresetTemplateRequirements?.installSchedulingApps;
        const servicesData = await getServicesData({
          httpClient,
          errorHandler,
          linguistHeader,
        });
        result.servicesData = servicesData;
      })(),
    );
  }
  if (isSchedulingRequiredForTemplateId(templateId)) {
    fetchDataPromises.push(
      (async () => {
        await handlePresetTemplateRequirements?.installSchedulingApps;
        const schedulingData = await getSchedulingData({
          httpClient,
          errorHandler,
        });
        result.schedulingData = schedulingData;
      })(),
    );
  }
  await Promise.all(fetchDataPromises);
  return result;
};
